import { Component, OnInit, Input, SimpleChanges, OnChanges, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { AuftragEx, AuftragListItem } from 'src/app/api/model/model';
import { AuftragHelper } from 'src/app/api/helper/auftrag-helper';
import { Router } from '@angular/router';
import { ClickHelper } from 'src/app/api/helper/click-helper';
import { AuftragService } from 'src/app/api/auftrag.service';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { Auftrag, Auftragstyp } from 'src/app/api/model/swagger-model';
import { MyApp } from 'src/app/api/helper/app';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AppConfig } from 'src/app/api/helper/app.config';
import { StammdatenService } from 'src/app/api/stammdaten.service';
import { Utils } from 'src/app/api/helper/utils';

@Component({
    selector: 'app-auftragitem',
    templateUrl: './auftragitem.component.html',
    styleUrls: ['./auftragitem.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AuftragitemComponent implements OnInit, OnChanges, OnDestroy {
    log = new Logger("AuftragitemComponent");

    @Input() auftrag: AuftragListItem;
    @Input() itemclass: string;
    @Input() ansicht: any;
    @Input() reorder = false;

    lastAuftrag: AuftragListItem;
    isStandardAnsicht = false;
    isHofauftraegeAnsicht = false;
    isKommissionierungAnsicht = false;
    isBeladungAnsicht = false;

    isZeile1 = true;
    isZeile2 = true;
    isZeile3 = true;
    isAbholdatum = true;

    /** Adresse und Abholdatum stehen rechts neben Name und Info-Texten. */
    isKompaktLayout = false;

    vo: AuftragVO = null;

    reihenfolgeAnzeigen: BehaviorSubject<boolean>;

    unterMandant = '';
    iconSrc = '';
    routerLink = '';

    subscriptions: Subscription[] = [];

    minItemHeight = undefined;
    minLabelHeight = undefined;
    maxLabelHeight = undefined;

    constructor(
        private router: Router,
        private cdr: ChangeDetectorRef,
        private stammdatenService: StammdatenService,
        private auftragService: AuftragService) {
        this.reihenfolgeAnzeigen = MyApp.current.TransportauftragReihenfolgeAnzeigen;
    }

    ngOnInit() {
        this.subscriptions.push(this.auftragService.auftragGeaendert.subscribe(a => this.onAuftragGeaendert(a)));
        this.subscriptions.push(MyApp.current.configRefreshed.subscribe(() => this.updateConfig()));

        this.updateConfig();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.subscriptions = [];
    }

    ngOnChanges(changes: SimpleChanges) {
        // this.log?.debug('ngOnChanges', changes);

        if (changes.auftrag) {
            this.updateVo();
        }
    }

    private updateConfig() {
        this.minItemHeight = MyApp.current.auftragZeilenhoeheMin + 'px';

        if (MyApp.current.auftragZeilenhoeheMin) {
            this.minLabelHeight = (MyApp.current.auftragZeilenhoeheMin - 25) + 'px';
        }

        if (MyApp.current.auftragZeilenhoehe === 'Fix') {
            this.maxLabelHeight = this.minLabelHeight;
        } else {
            this.maxLabelHeight = undefined;
        }

        // Nur die Standard-Ansicht kennt eine Adresse und damit eine rechte Spalte.
        this.isKompaktLayout = MyApp.current.auftragListenLayout === 'kompakt' && this.isStandardAnsicht;

        if (this.auftrag?.Auftragstyp == Auftragstyp.Hofchecker) {
            // Hofaufträge
            this.isZeile1 = MyApp.current.hofauftragZeile1Anzeigen;
            this.isZeile2 = MyApp.current.hofauftragZeile2Anzeigen;
            this.isZeile3 = MyApp.current.hofauftragZeile3Anzeigen;
            this.isAbholdatum = MyApp.current.hofauftragAbholdatumAnzeigen;
        } else {
            // Transportaufträge
            this.isZeile1 = MyApp.current.transportauftragZeile1Anzeigen;
            this.isZeile2 = MyApp.current.transportauftragZeile2Anzeigen;
            this.isZeile3 = MyApp.current.transportauftragZeile3Anzeigen;
            this.isAbholdatum = MyApp.current.transportauftragAbholdatumAnzeigen;
        }

        this.cdr.detectChanges();
    }

    async updateVo() {
        // this.log?.debug(`updateVo ${this.lastAuftrag?.Key} (${this.lastAuftrag?.Reihenfolge}) => ${this.auftrag?.Key} (${this.auftrag?.Reihenfolge})`);

        if (this.auftrag) {
            const adresse = this.auftrag.Abholadresse;

            this.vo = {
                auftrag: this.auftrag,
                abholdatum: AuftragHelper.getAbholdatumText(this.auftrag),
                className: AuftragHelper.getAbholdatumClassName(this.auftrag),
                uhrzeitVon: this.auftrag.AAbholzeitVon,
                uhrzeitBis: this.auftrag.AAbholzeitBis,
                adresseEinzeilig: adresse
                    ? Utils.joinNotEmpties(', ', adresse.Strasse, Utils.joinNotEmpties(' ', adresse.PLZ, adresse.Ort))
                    : '',
                style: ''
            };

            if (this.auftrag.zeiterfassungLaeuft) {
                if (AppConfig.current.darkMode) {
                    this.vo.style = '--background: #4d4d4d; ';
                } else {
                    this.vo.style = '--background: #A9DDB1; ';
                }
            } else if (this.istUeberfaellig()) {
                // Auftrag, dessen Abholdatum mehr als einen Tag zurückliegt, rot hervorheben.
                this.vo.style = '--background: #F1948A; ';
            } else if (this.auftrag.auftragsFarbe) {
                this.vo.style = '--background: ' + this.auftrag.auftragsFarbe + '; ';
            } else if (this.auftrag.Farbe) {
                // Vom ERP gelieferte Auftragsfarbe (z.B. Blau für bestimmte Artikel).
                this.vo.style = '--background: ' + this.auftrag.Farbe + '; ';
            } else if (this.auftrag.ERPStatusFarbe && AppConfig.current.einstellungen.AuftragERPStatusFarbeVerwenden === 'ja') {
                this.vo.style = '--background: ' + this.auftrag.ERPStatusFarbe + '; ';
            }

            if (!this.auftrag.Gelesen) {
                this.itemclass = 'auftrag-neu';
            }

            if (this.auftrag.istAusgeblendet) {
                this.itemclass += ' auftrag-ausgeblendet';
            }

            this.unterMandant = '';

            if (this.auftrag.UnterMandant) {
                if (AppConfig.current.UnterMandant) {
                    if (this.auftrag.UnterMandant.trim().toUpperCase() !== AppConfig.current.UnterMandant.trim().toUpperCase()) {
                        // Es handelt sich um einen Auftrag von einem anderen Untermandanten als das Gerät zugeordnet ist
                        this.unterMandant = this.auftrag.UnterMandant;
                    }
                }
            }

            this.cdr.detectChanges();

            if (this.auftrag.Auftragstyp === Auftragstyp.Kommissionierung) {
                if (this.auftrag.PersonalKey) {
                    const fahrerListe = await this.stammdatenService.getFahrerListe(false);
                    const personal = fahrerListe.find(p => p.Key === this.auftrag.PersonalKey);

                    if (personal && personal.Icon64) {
                        this.iconSrc = Utils.getImgSrc(personal.Icon64);
                    } else {
                        this.iconSrc = '/assets/img/no-avatar-64.png';
                    }
                } else {
                    this.iconSrc = '/assets/img/no-avatar-64.png';
                }
            }

            if (this.reorder) {
                this.routerLink = undefined;
            } else {
                if (this.ansicht === 'beladung') {
                    this.routerLink = '/auftragsliste/beladung/details/' + this.vo.auftrag.Key;
                } else {
                    this.routerLink = '/auftragsliste/details/' + this.vo.auftrag.Key;
                }
            }

            this.isStandardAnsicht = false;
            this.isHofauftraegeAnsicht = false;
            this.isKommissionierungAnsicht = false;
            this.isBeladungAnsicht = false;

            if (this.ansicht === 'beladung') {
                this.isBeladungAnsicht = true;
            } else if (this.ansicht === 'kommissionierung' || this.ansicht == 3) {
                this.isKommissionierungAnsicht = true;
            } else if (this.ansicht == 2) {
                this.isHofauftraegeAnsicht = true;
            } else {
                this.isStandardAnsicht = true;
            }

            this.cdr.detectChanges();
        } else {
            this.vo = null;
        }

        this.lastAuftrag = this.auftrag;
    }

    /**
     * Liefert TRUE, wenn das Abholdatum des Auftrags mehr als einen Tag zurückliegt.
     * Aufträge ohne Abholdatum (abholdatumTimestamp === 1) gelten nicht als überfällig.
     */
    private istUeberfaellig(): boolean {
        if (AppConfig.current.einstellungen.AuftragUeberfaelligRotAnzeigen !== 'ja') {
            return false;
        }

        const timestamp = this.auftrag?.abholdatumTimestamp;

        if (!timestamp || timestamp <= 1) {
            return false;
        }

        const einTagInMs = 24 * 60 * 60 * 1000;

        return timestamp < (Date.now() - einTagInMs);
    }

    async gotoItem(e: Event) {
        if (this.reorder) {
            return;
        }

        this.router.navigateByUrl(this.routerLink);
    }

    debugEvent(e: Event) {
        this.log?.info('EVENT: ' + e.type, e);
    }

    onAuftragGeaendert(a: AuftragListItem | AuftragEx) {
        if (this.auftrag && a?.Key === this.auftrag.Key) {
            // Workaround: Die Instanz in der Liste stimmt nicht mehr mit der aktuellsten Instanz überein.
            // Übernehme die relevanten Änderungen in unsere Instanz
            this.auftrag.auftragsFarbe = a.auftragsFarbe;
            this.auftrag.ERPStatusFarbe = a.ERPStatusFarbe;
            this.auftrag.Farbe = a.Farbe;
            this.auftrag.Gelesen = a.Gelesen;
            this.auftrag.zeiterfassungLaeuft = a.zeiterfassungLaeuft;
            this.auftrag.zeiterfassungUnterbrochen = a.zeiterfassungUnterbrochen;
            this.auftrag.AAbholzeitVon = a.AAbholzeitVon;
            this.auftrag.AAbholzeitBis = a.AAbholzeitBis;

            this.updateVo();
        }
    }
}

interface AuftragVO {
    auftrag: AuftragListItem;
    abholdatum: string;
    uhrzeitVon: string;
    uhrzeitBis: string;
    className: string;

    /** Strasse, PLZ und Ort in einer Zeile - für das kompakte Layout. */
    adresseEinzeilig: string;

    style: string;
}
